/**
 * poiMarkers.ts — POI marker icon assets + rasterization (T-179)
 *
 * Turns each of the 13 canonical POI categories into a pre-composited "map pin chip":
 * a category-color disc (the existing CATEGORY_COLORS tint) + a white Maki glyph + a
 * white ring + a soft drop shadow, registered with `map.addImage` so the POI symbol
 * layers can reference it by `icon-image`. The glyph is a redundant semantic channel on
 * top of the color, which materially helps color-blind users versus the old color-only
 * dots.
 *
 * Icon source: Maki (github.com/mapbox/maki), CC0-1.0 (public-domain dedication — see
 * markers/LICENSE.md). Only the SVGs we use are vendored; there is NO runtime icon
 * dependency. CATEGORY_COLORS stays the single source of truth for color — the chips
 * are built from it, so the LayersControl legend keeps its meaning.
 *
 * The pure surface (CATEGORY_COLORS, poiImageId, iconImageExpression) is unit-tested.
 * rasterizeChip / registerPoiMarkerImages are browser-only (they use <canvas> + Image)
 * and are proven by live WebGL rendering; in jsdom / SSR they no-op so the symbol layer
 * still mounts (icons attach in a real browser once rasterized).
 */
import type { Map as MaplibreMap } from 'maplibre-gl'

import cafeSvg from './markers/cafe.svg?raw'
import chargingStationSvg from './markers/charging-station.svg?raw'
import cinemaSvg from './markers/cinema.svg?raw'
import collegeSvg from './markers/college.svg?raw'
import fitnessCentreSvg from './markers/fitness-centre.svg?raw'
import fuelSvg from './markers/fuel.svg?raw'
import grocerySvg from './markers/grocery.svg?raw'
import hospitalSvg from './markers/hospital.svg?raw'
import lodgingSvg from './markers/lodging.svg?raw'
import parkingSvg from './markers/parking.svg?raw'
import restaurantSvg from './markers/restaurant.svg?raw'
import schoolSvg from './markers/school.svg?raw'
import shopSvg from './markers/shop.svg?raw'
import townHallSvg from './markers/town-hall.svg?raw'

// Canonical 13-category palette (must match the server's POI_CATEGORIES names in
// apps/api/src/lib/alsPoi.ts). This is the single source of truth for POI color and is
// re-exported from POILayerMgr for the LayersControl legend. Schools (K-12) was added in
// T-451 (F1), ordered after University to mirror the server taxonomy; it defaults ON like
// every sibling because MapShell seeds the enabled-category filter with all of these names.
export const CATEGORY_COLORS: Record<string, string> = {
  Restaurant: '#f97316',
  Café: '#a855f7',
  'Gas Station': '#ef4444',
  Parking: '#3b82f6',
  'Hotel/Motel': '#6366f1',
  Shopping: '#10b981',
  Grocery: '#22c55e',
  'Gym/Fitness': '#f59e0b',
  Cinema: '#ec4899',
  Hospital: '#dc2626',
  University: '#0d9488',
  Schools: '#ca8a04', // warm gold — distinct from University teal and the amber Gym chip
  Civic: '#64748b',
}

// Canonical category names in display order (single source of truth for filters/loops).
export const POI_CATEGORY_NAMES = Object.keys(CATEGORY_COLORS)

// Category → Maki icon slug (each verified to exist in mapbox/maki; the SVGs are
// vendored under ./markers). Higher-ed only for University; Civic groups library /
// city-hall / courthouse under the town-hall glyph.
const CATEGORY_ICON_SLUG: Record<string, string> = {
  Restaurant: 'restaurant',
  Café: 'cafe',
  'Gas Station': 'fuel',
  Parking: 'parking',
  'Hotel/Motel': 'lodging',
  Shopping: 'shop',
  Grocery: 'grocery',
  'Gym/Fitness': 'fitness-centre',
  Cinema: 'cinema',
  Hospital: 'hospital',
  University: 'college',
  Schools: 'school',
  Civic: 'town-hall',
}

const SVG_BY_SLUG: Record<string, string> = {
  restaurant: restaurantSvg,
  cafe: cafeSvg,
  fuel: fuelSvg,
  parking: parkingSvg,
  lodging: lodgingSvg,
  shop: shopSvg,
  grocery: grocerySvg,
  'fitness-centre': fitnessCentreSvg,
  cinema: cinemaSvg,
  hospital: hospitalSvg,
  college: collegeSvg,
  school: schoolSvg,
  'town-hall': townHallSvg,
}

// Fallback slug for any category not in the canonical set (mirrors the old '#94a3b8'
// color fallback — a defined marker rather than a missing image).
const FALLBACK_SLUG = 'town-hall'

/** Maki slug for a category (unique across the canonical set). */
export function poiCategorySlug(category: string): string {
  return CATEGORY_ICON_SLUG[category] ?? FALLBACK_SLUG
}

/** Stable, unique MapLibre image id for a category's pre-composited chip. */
export function poiImageId(category: string): string {
  return `poi-icon-${poiCategorySlug(category)}`
}

/**
 * Data-driven ['match', ['get','category'], cat, imageId, …, fallbackImageId] used as the
 * symbol layer's icon-image — the direct sibling of the old categoryColorExpression.
 */
export function iconImageExpression(): unknown[] {
  const flat: string[] = []
  for (const cat of POI_CATEGORY_NAMES) flat.push(cat, poiImageId(cat))
  return ['match', ['get', 'category'], ...flat, poiImageId('Civic')]
}

// Marker geometry (CSS px) — matches the approved design mock: 26 px chip, ~58% glyph,
// 1.25 px white ring, soft shadow, rasterized at pixelRatio 2 for retina crispness.
const MARKER_DIAMETER = 26
const MARKER_PIXEL_RATIO = 2

function canvas2dSupported(): boolean {
  try {
    const c = document.createElement('canvas')
    return typeof c.getContext === 'function' && c.getContext('2d') !== null
  } catch {
    return false
  }
}

/**
 * Composite one category chip: white ring + soft shadow, colored disc, centered white
 * glyph. Returns an ImageData at pixelRatio 2, or null if 2D canvas is unavailable
 * (jsdom / SSR). Browser-only path — proven by live WebGL rendering.
 */
async function rasterizeChip(
  color: string,
  svgText: string,
): Promise<{ image: ImageData; pixelRatio: number } | null> {
  const R = MARKER_PIXEL_RATIO
  const D = MARKER_DIAMETER
  const W = D * R
  const H = D * R
  const cv = document.createElement('canvas')
  cv.width = W
  cv.height = H
  const ctx = cv.getContext('2d')
  if (!ctx) return null

  // Force every drawable shape in the Maki glyph to white regardless of its own fills.
  const whiteSvg = svgText.replace(
    /<svg([^>]*)>/i,
    '<svg$1><style>*{fill:#ffffff!important;stroke:none!important;}</style>',
  )
  const glyph = await new Promise<HTMLImageElement>((res, rej) => {
    const img = new Image()
    img.onload = () => res(img)
    img.onerror = rej
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(whiteSvg)
  })

  const cx = W / 2
  const cy = H / 2
  const rad = (D / 2 - 1.5) * R

  // Soft drop shadow behind a white ring (the ring is the two-theme legibility device —
  // it separates the chip from both the light and dark basemaps).
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.35)'
  ctx.shadowBlur = 3 * R
  ctx.shadowOffsetY = 1 * R
  ctx.beginPath()
  ctx.arc(cx, cy, rad + 1.25 * R, 0, Math.PI * 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  ctx.restore()

  // Colored category chip.
  ctx.beginPath()
  ctx.arc(cx, cy, rad, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()

  // Centered white glyph (~58% of the chip).
  const g = rad * 1.15
  ctx.drawImage(glyph, cx - g / 2, cy - g / 2, g, g)

  return { image: ctx.getImageData(0, 0, W, H), pixelRatio: R }
}

/**
 * Register one chip image per category with map.addImage. No-ops when 2D canvas is
 * unavailable (jsdom / SSR) so unit tests and the symbol layer mount unaffected; icons
 * attach in a real browser once rasterized (MapLibre tolerates a not-yet-added image).
 */
export async function registerPoiMarkerImages(map: MaplibreMap): Promise<void> {
  if (!canvas2dSupported()) return
  for (const cat of POI_CATEGORY_NAMES) {
    const id = poiImageId(cat)
    if (map.hasImage(id)) continue
    try {
      const built = await rasterizeChip(
        CATEGORY_COLORS[cat]!,
        SVG_BY_SLUG[CATEGORY_ICON_SLUG[cat]!]!,
      )
      // The style may have been torn down (sign-out) while an await was pending.
      if (built && !map.hasImage(id))
        map.addImage(id, built.image, { pixelRatio: built.pixelRatio })
    } catch {
      // A single glyph failing to rasterize must not abort the rest.
    }
  }
}

/** Remove every registered POI chip image (teardown). */
export function removePoiMarkerImages(map: MaplibreMap): void {
  for (const cat of POI_CATEGORY_NAMES) {
    const id = poiImageId(cat)
    if (map.hasImage(id)) map.removeImage(id)
  }
}

// ─── EV charging-station chips (T-214) ────────────────────────────────────────
// The EV charging layer (EVChargerLayer) is a SEPARATE source from the POI overlay,
// but Ricardo asked its "power stations" to carry an icon like the T-179 POIs. So we
// reuse this module's exact chip pipeline (Maki glyph + colored disc + white ring) for
// the charging-station glyph, tinted by the SAME 3-level color logic the EV circle paint
// already uses (DC-fast → green, Level 2 → blue, else Level 1 → slate). The chip rides on
// top of the kept, port-count-sized circle, so the size channel is preserved.
export const EV_LEVEL_COLORS: Record<string, string> = {
  dc: '#16a34a', // DC Fast — green
  l2: '#2563eb', // Level 2 — blue
  l1: '#94a3b8', // Level 1 / unknown — slate
}

// Level keys in descending capability order (single source of truth for loops/expressions).
export const EV_LEVELS = Object.keys(EV_LEVEL_COLORS)

// Cluster chip tint — the official brand teal (finding g), kept so a collapsed pile of
// stations reads as one branded charging chip with a count badge (the T-179 cluster look),
// not a plain bubble. Mixed levels have no single color, so the neutral brand teal is used.
export const EV_CLUSTER_COLOR = '#45b4ac'

/** Stable, unique MapLibre image id for an EV charger chip at a given level (L1 fallback). */
export function evImageId(level: string): string {
  return `poi-ev-${EV_LEVEL_COLORS[level] ? level : 'l1'}`
}

/** Image id for the brand-teal cluster chip (a collapsed pile of stations). */
export function evClusterImageId(): string {
  return 'poi-ev-cluster'
}

/**
 * Data-driven ['match', ['get','level'], 'dc', id, …, fallbackId] used as the EV symbol
 * layer's icon-image — the direct analog of iconImageExpression() for the POI layer.
 */
export function evIconImageExpression(): unknown[] {
  const flat: string[] = []
  for (const level of EV_LEVELS) flat.push(level, evImageId(level))
  return ['match', ['get', 'level'], ...flat, evImageId('l1')]
}

/**
 * Register one charging-station chip image per level with map.addImage. No-ops when 2D
 * canvas is unavailable (jsdom / SSR) — mirrors registerPoiMarkerImages exactly.
 */
export async function registerEvMarkerImages(map: MaplibreMap): Promise<void> {
  if (!canvas2dSupported()) return
  // The 3 level chips (single stations) + the brand-teal cluster chip.
  const chips: Array<[string, string]> = [
    ...EV_LEVELS.map((level): [string, string] => [evImageId(level), EV_LEVEL_COLORS[level]!]),
    [evClusterImageId(), EV_CLUSTER_COLOR],
  ]
  for (const [id, color] of chips) {
    if (map.hasImage(id)) continue
    try {
      const built = await rasterizeChip(color, chargingStationSvg)
      // The style may have been torn down (sign-out) while an await was pending.
      if (built && !map.hasImage(id))
        map.addImage(id, built.image, { pixelRatio: built.pixelRatio })
    } catch {
      // A single glyph failing to rasterize must not abort the rest.
    }
  }
}

/** Remove every registered EV charger chip image (teardown). */
export function removeEvMarkerImages(map: MaplibreMap): void {
  const ids = [...EV_LEVELS.map(evImageId), evClusterImageId()]
  for (const id of ids) {
    if (map.hasImage(id)) map.removeImage(id)
  }
}
